import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";
import styled from "styled-components";

import {
  IDrop,
  IOrderModeSetting
} from "src/core/drops/api/drops.api";
import SelectedDrop from "src/core/drops/components/SelectedDrop";
import {
  getOrderMode,
  getSingleOrder
} from "src/core/drops/utils/inherited-settings";
import {
  IOrder,
  IOrderVariant
} from "src/core/orders/api/orders.api";
import { ConfirmMultipleOrderModal } from "src/core/orders/components/Modals";
import SelectOrderVariant from "src/core/orders/components/SelectOrderVariant";
import { useOrderingAvailableForDay } from "src/core/orders/hooks/validation";
import {
  IProduct,
  ProductStatus
} from "src/core/products/api/products.api";
import type { IStore } from "src/core/shared/store";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  ActionsPopOut,
  IStep
} from "src/library/components/ActionsPopOut/index";
import { Button } from "src/library/components/Button";
import { ButtonType } from "src/library/components/Button/Button";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Modal } from "src/library/components/Modal";
import { useEventListener } from "src/library/hooks/useEventListener";
import { useWindowSize } from "src/library/hooks/useWindowSize";
import { hasOrders } from "src/library/utils/date-helpers";
import { useCurrentUser } from "src/core/users/hooks";
import { UserOrderableDropSelect } from "src/core/drops/components/UserOrderableDropMultiSelect";

import CreateOrderCalendar, { CreateOrderCalendarRef } from "./CreateOrderCalendar";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { detect } = require("detect-browser");

interface IOrderContextProps {
  order?: IOrder | null;
  createDraftOrder: (deliveryDate: Date | null, options?: { drop?: IDrop | null, orderVariant?: IOrderVariant | null }) => void;
  loading: boolean;
  product?: IProduct;
  drop: IDrop | null;
  direction: "right" | "left" | "overlay";
  isHeader?: boolean;
  $buttonType?: ButtonType;
  icon?: boolean;
  requireDropSelection?: boolean
  onOrderModalChange?: (isOpen: boolean) => void;
  defaultVariantId?: number;
  potentialDrops?: IDrop[]
}

export const CreateOrderContext: React.ReactFCWithChildren<IOrderContextProps> = ({
  createDraftOrder,
  product,
  loading,
  drop,
  direction,
  onOrderModalChange,
  isHeader = false,
  $buttonType = "primary",
  icon = true,
  order,
  requireDropSelection = false
}) => {
  const {
    terminology, installedModules, orderVariants, userDrops
  } = useMappedState(({ global, drops }: IStore) => ({
    terminology: global.settings?.terminology,
    installedModules: global.settings?.installedModules || [],
    orderVariants: global.settings?.orderVariants || [],
    userDrops: drops.userDrops
  }));

  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  // only used in the admin drop selection step
  const [ manuallySelectedDrop, setManuallySelectedDrop ] = React.useState<IDrop | null>(null);
  const [ { currentRole } ] = useCurrentUser();

  const selectedDrop = React.useMemo(() => {
    return manuallySelectedDrop || (currentRole?.code !== "customer-admin" ? drop : null);
  }, [
    currentRole?.code,
    drop,
    manuallySelectedDrop
  ]);

  const orderingNotAvailableForDay = useOrderingAvailableForDay(selectedDrop);
  const browser = detect();
  const divRef = React.useRef<HTMLDivElement>(null);
  const history = useHistory();
  const [ modalData, setModalData ] = React.useState<string | null>(null);
  const [ showMultipleOrderModal, setShowMultipleOrderModal ] = React.useState(false);
  const ordersCalendarRef = React.useRef<CreateOrderCalendarRef>(null);

  const { orderDates } = React.useMemo(() => ({
    orderDates: ordersCalendarRef.current?.orderDates ?? [],
    currentOrders: ordersCalendarRef.current?.currentOrders ?? []
  }), [ ordersCalendarRef ]);

  const singleDailyDelivery = selectedDrop && getSingleOrder(selectedDrop.groupedSettings);
  const orderMode = selectedDrop ? getOrderMode(selectedDrop.groupedSettings) : null;
  const [ showAction, setShowAction ] = React.useState(false);
  const [ orderDate, setOrderDate ] = React.useState<Date | null>(null);
  const [ selectedOrderMode, setSelectedOrderMode ] = React.useState<IOrderModeSetting["allow"] | null>(requireDropSelection ? "DL" : orderMode ? orderMode.allow : null);
  const canCreateOrderPermission = useCheckUserPermission();

  //gets the branches based on the prices associated to the product selected
  const branchesToUseBasedOnProduct = React.useMemo(() => {
    if (userDrops && product?.id) {
      const allProductPriceCodes = _.uniq(product.prices?.map(pri => pri.priceCodeId));
      const dropsToUse = userDrops.filter(drop => allProductPriceCodes.includes(drop.priceCodeId));

      if (dropsToUse.length === 1) {
        setManuallySelectedDrop(dropsToUse[ 0 ]);
      }

      return dropsToUse;
    } else return null;
  }, [
    product?.id,
    product?.prices,
    userDrops
  ]);

  const [ selectedOrderVariantId, setSelectedOrderVariantId ] = React.useState<number | null>(null);

  const selectedOrderVariant = React.useMemo(() => {
    if (selectedDrop?.variantsAvailable?.length === 1) {
      return selectedDrop.variantsAvailable[ 0 ];
    }

    return _.find(orderVariants, ov => ov.id === selectedOrderVariantId);
  }, [
    orderVariants,
    selectedDrop?.variantsAvailable,
    selectedOrderVariantId
  ]);

  const windowSize = useWindowSize();
  const [ isCollapsed, setIsCollapsed ] = React.useState(false);

  React.useEffect(() => {
    if (order) {
      setShowAction(true);
    }
  }, [ order ]);

  React.useEffect(() => {
    if (windowSize.width) {
      if (windowSize.width < 1024) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    }
  }, [ windowSize.width ]);

  const toggleShowAction = React.useCallback(() => {
    if (requireDropSelection || currentRole?.code === "customer-admin") {
      setShowAction(currShowAction => !currShowAction);
    } else if (orderMode) {
      // Don't create dateless draft order if RJL installed -
      // We don't want to link RJL delivery numbers to orders without a date set
      if ((orderMode.allow === "MTO") && !installedModules?.includes("rjl")) {
        let productOrderVariant: IOrderVariant | null = null;

        if (product) {
          productOrderVariant = _.find(orderVariants, ov => ov.id === product.orderVariantId) || null;

          if (productOrderVariant) {
            setSelectedOrderVariantId(productOrderVariant.id);
          }
        }

        // Jump straight into order
        createDraftOrder(orderDate, {
          drop: selectedDrop,
          orderVariant: productOrderVariant
        });
      } else {
        // Open up action dropdown for user:
        setShowAction(currShowAction => !currShowAction);
        setSelectedOrderMode(orderMode.allow);
      }
    }
  }, [
    requireDropSelection,
    currentRole?.code,
    orderMode,
    product,
    installedModules,
    createDraftOrder,
    orderDate,
    selectedDrop,
    orderVariants
  ]);

  const setOverlayPosition = React.useCallback((autoScroll = false) => {
    if (divRef.current) {
      const bounds = divRef.current.getBoundingClientRect();
      let right = bounds.right - bounds.width;

      if (direction === "overlay") {
        setPosition({
          y: bounds.top - 378,
          x: bounds.right - 400
        });

        return;
      }

      if (window.outerWidth <= 500) {
        right = 0;
      } else if (bounds.right + 400 > window.outerWidth) {
        right = bounds.right - 310;
      }

      setPosition({
        y: bounds.top + bounds.height,
        x: right
      });

      if (autoScroll && browser.os !== "iOS") {
        const scrollWrapper = document.getElementById("main-wrapper");

        if (scrollWrapper) {
          const currentBottom = bounds.top + 350;
          const currentScroll = scrollWrapper.scrollTop;

          if (currentBottom > window.outerHeight) {
            scrollWrapper.scrollTo({
              top: currentScroll + (currentBottom - window.outerHeight),
              behavior: "smooth"
            });
          }
        }
      }
    }
  }, [ browser.os, direction ]);

  React.useEffect(() => {
    if (showAction) {
      setOverlayPosition(true);
    }

    if (onOrderModalChange) {
      onOrderModalChange(showAction);
    }
  }, [
    onOrderModalChange,
    setOverlayPosition,
    showAction
  ]);

  const handler = React.useCallback(() => {
    setOverlayPosition();
  }, [ setOverlayPosition ]);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));
  useEventListener("scroll", handler, document.getElementsByClassName("rt-table")[ 0 ]);

  const dateLedOrderingDayPicker: IStep = React.useMemo(
    () => ({
      title: "Choose your delivery date",
      component: selectedDrop ? (
        <CreateOrderCalendar
          orderVariantId={selectedOrderVariant?.id}
          drop={selectedDrop}
          handleDateChange={setOrderDate}
          ref={ordersCalendarRef}
        />
      ) : <></>,
      allowNext: orderDate !== null && !!selectedOrderVariant && (orderingNotAvailableForDay(orderDate, selectedOrderVariant, product) === null)
    }),
    [
      selectedDrop,
      orderDate,
      orderingNotAvailableForDay,
      product,
      selectedOrderVariant
    ]
  );

  const dropSelectionStep = React.useMemo(() => {
    //  customer admin drop selection
    if (currentRole?.code === "customer-admin") {
      return {
        title: `Which ${terminology?.drop.singular.text} would you like to order for?`,
        component: (
          <SelectDropWrapper>
            <UserOrderableDropSelect
              onDropsChange={drop => setManuallySelectedDrop(drop)}
              selectedDrop={selectedDrop}
              orderVariantId={selectedOrderVariantId}
              preFilters={branchesToUseBasedOnProduct ? { branchNumber: { $in: branchesToUseBasedOnProduct?.map(b => b.branchNumber) } } : undefined}
            />
          </SelectDropWrapper>

        ),
        hideFooter: false,
        allowNext: !!selectedDrop
      };
    //  standard customer drop selection
    } else if (requireDropSelection) {
      const dropSelectionStep: IStep = {
        title: `Select ${terminology?.drop.singular.text}`,
        allowNext: true,
        hideFooter: false,
        component: (
          <SelectDropWrapper>
            <SelectedDrop
              drop={selectedDrop || null}
              handleUpdate={newDrop => {
                setManuallySelectedDrop(newDrop);
              }}
              terminology={terminology}
              placeHolder={`Please Select ${_.startCase(terminology?.drop.singular.text)}`}
            />
          </SelectDropWrapper>
        )
      };

      return dropSelectionStep;
    }

    return null;
  }, [
    selectedDrop,
    requireDropSelection,
    currentRole?.code,
    selectedOrderVariantId,
    branchesToUseBasedOnProduct,
    terminology
  ]);

  // Step to select what type of order variant the order will be
  const orderVariantSelectionStep = React.useMemo(() => {
    if ((selectedDrop?.variantsAvailable && selectedDrop?.variantsAvailable.length > 1 && !product) || (!product && installedModules.includes("zertus"))) {
      const variantSelectionStep: IStep = {
        title: "Select type of order",
        allowNext: true,
        hideFooter: false,
        component: (
          <SelectOrderVariant
            availableOrderVariants={selectedDrop?.variantsAvailable}
            orderVariant={selectedOrderVariant ?? undefined}
            handleUpdate={setSelectedOrderVariantId}
          />
        )
      };

      return variantSelectionStep;
    }

    return null;
  }, [
    product,
    selectedDrop,
    installedModules,
    selectedOrderVariant
  ]);

  const createOrderSteps = React.useMemo(() => {
    const steps: IStep[] = [];

    // zertus need to select order variant first
    if (orderVariantSelectionStep && installedModules.includes("zertus")) {
      steps.push(orderVariantSelectionStep);
    }

    //  drop selection if required
    if (dropSelectionStep) {
      steps.push(dropSelectionStep);
    }

    //  order variant selection if this is a zertus instance, we have already picked it
    if (orderVariantSelectionStep && !installedModules.includes("zertus")) {
      steps.push(orderVariantSelectionStep);
    }

    /**
     * Switch to handle what kind of action dropdown
     * should be displayed. If any, the user needs to choose
     * either DL (and show a calendar) or MTO (and jump
     * straight into the draft order)
     */
    switch (selectedOrderMode) {
      case "ANY" || null:
        steps.push(
          {
            title: "How would you like to order?",
            component: (
              <OrderModeAnyWrapper>
                <Button onClick={() => setSelectedOrderMode("DL")}>
                  Select delivery date first
                </Button>

                <Button onClick={() => createDraftOrder(null, { orderVariant: selectedOrderVariant })}>
                  Select products first
                </Button>
              </OrderModeAnyWrapper>
            ),
            allowNext: true,
            hideFooter: false
          }
        );
        break;
      case "DL":
        steps.push(dateLedOrderingDayPicker);
        break;
      default:
        steps.push(dateLedOrderingDayPicker);
        break;
    }

    return steps;
  }, [
    orderVariantSelectionStep,
    installedModules,
    dropSelectionStep,
    selectedOrderMode,
    dateLedOrderingDayPicker,
    createDraftOrder,
    selectedOrderVariant
  ]);

  const handleCreateOrderAction = React.useCallback(() => {
    if (orderDate) {
      createDraftOrder(orderDate, {
        drop: selectedDrop,
        orderVariant: selectedOrderVariant
      });
      toggleShowAction();
    }
  }, [
    createDraftOrder,
    orderDate,
    selectedDrop,
    toggleShowAction,
    selectedOrderVariant
  ]);

  const actionsPopOutAction = React.useCallback(() => {
    if (orderDate && orderDates) {
      const hasOrderOnDate = hasOrders(orderDate, orderDates);

      // If one-order-per-day setting is true, and an order exists:
      if (singleDailyDelivery && singleDailyDelivery.value === true) {
        const orderDay = orderDates.find(or => moment.utc(or.date).isSame(orderDate, "day"));

        if (orderDay) {
          setModalData(`/orders/${orderDay.id}`);
        } else {
          handleCreateOrderAction();
        }
      } else if (hasOrderOnDate) {
        setShowMultipleOrderModal(true);
      } else {
        handleCreateOrderAction();
      }
    }
  }, [
    handleCreateOrderAction,
    orderDate,
    orderDates,
    singleDailyDelivery
  ]);

  return (
    <>
      <OrderContextWrapper>
        {isCollapsed && isHeader && icon ? (
          <Icon
            icon={Icons.CART}
            onClick={toggleShowAction}
          />
        ) : product ? (
          <Button
            $buttonType={$buttonType}
            icon={icon ? Icons.CART : undefined}
            onClick={() => toggleShowAction()}
            size="medium"
            loading={loading}
            disabled={product.currentStatus?.status === ProductStatus.Suspended || product.currentStatus?.status === ProductStatus.Delisted || (!product.prices?.length && currentRole?.code !== "customer-admin")}
          >
            {`Add to ${terminology?.cart?.singular.text || "cart"}`}
          </Button>
        ) : (
          <Button
            icon={Icons.CALENDAR}
            $buttonType={$buttonType}
            onClick={toggleShowAction}
            size="medium"
            loading={loading}
            disabled={!canCreateOrderPermission("create-order")}
          >
            Create order
          </Button>
        )}

        {!!showAction && (
          <ActionsPopOut
            directionHorizontal="left"
            directionVertical="top"
            position={position}
            steps={createOrderSteps}
            ref={divRef}
            disableAction={!canCreateOrderPermission("create-order")}
            hideCaret={direction === "overlay"}
            action={() => actionsPopOutAction()}
            handleClose={() => {
              setSelectedOrderVariantId(null);
              setManuallySelectedDrop(null);
              toggleShowAction();
            }}
            className="orders-popout"
          />
        )}
      </OrderContextWrapper>

      <Modal
        small
        actions={[
          {
            name: "Go back",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "Edit existing order",
            action: () => (modalData !== null ? history.push(modalData, { edit: true }) : null),
            $buttonType: "primary"
          }
        ]}
        backgroundAction={() => setModalData(null)}
        body="If you want to order more products for this date, you must edit the existing order for this date."
        type="confirm"
        title="You already have an order for this date"
        show={modalData !== null}
      />

      {orderDate && (
        <ConfirmMultipleOrderModal
          onDismiss={() => setShowMultipleOrderModal(false)}
          open={showMultipleOrderModal}
          onConfirm={() => handleCreateOrderAction()}
          orderDates={orderDates}
          selectedDay={orderDate}
          terminology={terminology}
        />
      )}
    </>
  );
};

const OrderModeAnyWrapper = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.padding.md};
  flex-direction: column;
  padding-top: 0;
  > * {
    margin-top: ${({ theme }) => theme.spacing.padding.sm};
  }
`;

const SelectDropWrapper = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.padding.md};
  flex-direction: column;
  padding-top: 0;
  > * {
    margin-top: ${({ theme }) => theme.spacing.padding.sm};
  }
`;

const OrderContextWrapper = styled.div`
  position: "relative";
`;
